import React from 'react'
import styled from 'styled-components'
import { OrderStatusHistoryResponse } from '../../../../../modules/API/Orders/models/orderStatusLog'
import { Orders } from '../../../../../modules/API/Orders/orders'
import useDataFetcher from '../../knisot/hooks/useFetchData'
import CustomBlockLoader from '../../knisot/generic/CustomBlockLoader'
import ArrowIcon from '../../../../../generic/icons/IconLib/ArrowIcon'
import CalendatDay from '../../../../../generic/icons/IconLib/CalendarIcon'
import UserIcon from '../../../../../generic/icons/IconLib/UserIcon'
import StatusBadge from '../../teudotProblems/Components/StatusBadge'
import { statusConverter } from '../../../../../generic/artcoded/statusAzmanot'

type Props = {
  orderId: string
}

const MOCK_DATA: OrderStatusHistoryResponse = {
  orderID: 102938,
  items: [
    {
      changed_at: '2026-03-04T12:34:56.789Z',
      fromStatus: 'פתוח',
      toStatus: 'ליקוט',
      user: {
        codeInternal: 156,
        name: 'Alex',
      },
    },
    {
      changed_at: '2026-03-04T13:10:12.120Z',
      fromStatus: 'ליקוט',
      toStatus: 'מוכנה למשלוח',
      user: {
        codeInternal: 200,
        name: 'Moshi',
      },
    },
  ],
}

type FormattedStatusDateTime = {
  date: string
  time: string
}

const formatStatusDateTime = (isoString: string): FormattedStatusDateTime | null => {
  const date = new Date(isoString)
  if (Number.isNaN(date.getTime())) return null

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = String(date.getFullYear())
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  return {
    date: `${day}/${month}/${year}`,
    time: `${hours}:${minutes}`,
  }
}

const OrderStatusLogModal = ({ orderId }: Props) => {
  const { data, isLoading } = useDataFetcher<OrderStatusHistoryResponse>(
    Orders.getOrderStatusHistory,
    orderId.toString()
  )

  if (isLoading) {
    return <CustomBlockLoader />
  }

  // const items = data?.items || MOCK_DATA.items

  if (data?.items?.length === 0) {
    return <EmptyMessage>אין היסטוריית סטטוסים להזמנה זו</EmptyMessage>
  }
  return (
    <StatusLogContainer role="list" aria-label="Order status history">
      {data?.items?.map((item, index) => {
        const formatted = formatStatusDateTime(item.changed_at)
        const fromStatus = statusConverter(item.fromStatus)
        const toStatus = statusConverter(item.toStatus)
        const ariaLabel = formatted
          ? `Status changed from ${item.fromStatus} to ${item.toStatus} by ${item.user.name} at ${formatted.date} ${formatted.time}`
          : `Status changed from ${item.fromStatus} to ${item.toStatus} by ${item.user.name}`

        return (
          <StatusCard key={`${item.changed_at}-${index}`} role="listitem" aria-label={ariaLabel}>
            <StatusInfo>
              <StatusRow>
                <StatusDateTime>
                  {formatted && (
                    <StatusDateTimeText>
                      <span>{formatted.time} {formatted.date}</span>
                    </StatusDateTimeText>
                  )}
                  <StatusDateIconWrapper>
                    <CalendatDay />
                  </StatusDateIconWrapper>
                </StatusDateTime>
                <StatusDetails>
                <StatusDetailItem>
                  <StatusDetailValue>
                    {item.user.name}
                  </StatusDetailValue>
                  <StatusDetailIcon>
                    <UserIcon />
                  </StatusDetailIcon>
                </StatusDetailItem>
              </StatusDetails>
              </StatusRow>
                <StatusTransition>
                  <StatusBadge statusColor={toStatus.color} color={'#808080'}>
                    {toStatus.status}
                  </StatusBadge>
                  <ArrowIcon position='left' />
                  <StatusBadge statusColor={fromStatus.color} color={'#808080'}>
                    {fromStatus.status}
                  </StatusBadge>
                </StatusTransition>
              
            </StatusInfo>
          </StatusCard>
        )
      })}
    </StatusLogContainer>
  )
}

const StatusLogContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75em;
  overflow-y: auto;
  padding: 0.5em;
  max-height: 500px;
  width: 100%;
`

const StatusCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5em;
  padding: 1em 1.25em;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }
`

const StatusInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5em;
  width: 100%;
`

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1em;
`

const StatusTransition = styled.span`
  color: #297f76;
  font-weight: 600;
  font-size: 0.95em;
  display: flex;
  justify-content: flex-end;
  gap: 0.5em;
  align-items: center;
`

const StatusDetails = styled.div`
  display: flex;
  gap: 1em;
  align-items: center;
  justify-content: flex-end;
`

const StatusDetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5em;
`

const StatusDetailIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #297f76;
  font-size: 0.9em;
`

const StatusDetailValue = styled.span`
  color: #2c3e50;
  font-weight: 500;
  font-size: 0.9em;
  display: flex;
  justify-content: flex-end;
`

const StatusDateTime = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5em;
`

const StatusDateIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #297f76;
  font-size: 1em;
  flex-shrink: 0;
`

const StatusDateTimeText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-size: 0.8em;
  color: #2c3e50;

  span:first-child {
    font-weight: 500;
  }
`

const EmptyMessage = styled.div`
  padding: 2em;
  text-align: center;
  color: #7f8c8d;
  font-size: 1em;
`

export default OrderStatusLogModal